// export default ListLibroMayor
import { useContext, useEffect, useState, useMemo } from 'react'
import GlobalContext from '../../../context/global-context'
import clsx from 'clsx'
import { sileo } from 'sileo'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'

//styles
import styles from './ListLibroMayor.module.css'

// MUI
import { Backdrop } from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

// Componentes de UI
import { HeadingTitle, Text } from '../../../components/modules/ui'
import CustomButton from '../../../components/modules/ui/CustomButton'
import SearchableSelect from '../../../components/modules/ui/SearchableSelect'
import ExportToXlsxModal from '../../../components/modules/modals/ExportToXlsxModal'
import businessApi from '../../../api/business.api'
import Table from './Table'

// Constantes de fecha y utilidades
import { currentMonth, currentYear, yearsOptions } from '../../../components/utils/constant'

// API
import { BusinessAPI } from '../../../api/business.api'

// Hooks
import { useInfiniteLibroMayor } from '../../../hooks/useInfiniteLibroMayor'
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll'

const ListLibroMayor = () => {
  const { currentBusiness } = useContext(GlobalContext)
  const businessAPI = new BusinessAPI()

  // 1) Estados de fecha (año/mes “Desde” y “Hasta”)
  const [yearFilter, setYearFilter] = useState(currentYear)
  const [dateFromMonth, setDateFromMonth] = useState(`${currentYear}-01`)
  const [dateToMonth, setDateToMonth] = useState(
    `${currentYear}-${String(currentMonth).padStart(2, '0')}`
  )

  // 2) Estados “Cuenta” (select)
  const [acount, setAcount] = useState([]) // Todas las cuentas del backend (sin "Todas")
  const [selectedAccount, setSelectedAccount] = useState(null) // Objeto cuenta seleccionado

  // 3) Estados de filtros adicionales
  const [comprobanteFilter, setComprobanteFilter] = useState('')
  const [clienteFilter, setClienteFilter] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [isExportModalOpen, setIsExportModalOpen] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // 4) Infinite query hook for data
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch
  } = useInfiniteLibroMayor({
    businessId: currentBusiness?.id || 0,
    idCuenta: selectedAccount?.id || 0,
    dateFrom: {
      year: parseInt(dateFromMonth.split('-')[0]),
      month: parseInt(dateFromMonth.split('-')[1])
    },
    dateTo: {
      year: parseInt(dateToMonth.split('-')[0]),
      month: parseInt(dateToMonth.split('-')[1])
    },
    limit: 30,
    filters: {
      searchQuery: '',
      comprobanteFilter,
      clienteFilter
    }
  })

  const { lastElementRef } = useInfiniteScroll({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  })

  const [showModal, setShowModal] = useState({ show: false, title: '', body: null })
  const Modal = () => {
    const { show, title, body } = showModal
    if (!show) return null
    return (
      <Backdrop sx={{ zIndex: 1600 }} open={true}>
        <div className='py-6 px-12 flex flex-col items-center justify-center bg-white'>
          <Text variant='h6' className='text-xl mb-4 text-center'>
            {title}
          </Text>
          {body}
          <button
            className='bg-primary text-white rounded-full px-4 py-2 hover:bg-primary/80 focus:outline-none shadow mt-4'
            onClick={() => setShowModal({ show: false, title: '', body: null })}
          >
            Cerrar
          </button>
        </div>
      </Backdrop>
    )
  }

  // -- Cambia aquí: Agrega la opción "Todas" al inicio del select pero selecciona la primera real --
  const getDataAcounts = async () => {
    if (!currentBusiness.id) {
      console.error('No hay un negocio seleccionado')
      setAcount([])
      setSelectedAccount(null)
      return
    }
    try {
      const { data } = await businessAPI.getCuentasUsadasLibroMayor({
        businessId: currentBusiness.id
      })
      setAcount(data || [])
      if (Array.isArray(data) && data.length > 0) {
        setSelectedAccount({ id: 0, codigo: 'Todas', name: 'Todas las cuentas' })
      }
    } catch (error) {
      console.error(error)
      setAcount([])
      setSelectedAccount(null)
    }
  }

  const toastMessageBank = () => {
    return sileo.promise(getDataAcounts(), {
      loading: { title: 'Cargando Cuentas' },
      success: {
        title: 'Cuentas Cargadas',
        description: (
          <span className='text-green-500/80 font-medium'>Cuentas cargadas exitosamente 👌</span>
        )
      },
      error: {
        title: 'Error de Carga',
        description: 'No se pudieron cargar las cuentas 🤯'
      }
    })
  }

  useEffect(() => {
    toastMessageBank()
  }, [currentBusiness.id])

  // 7) Data processing from infinite query
  const data = useMemo(() => {
    return infiniteData?.pages?.flatMap(page => page.data) || []
  }, [infiniteData])

  const handleYearChange = e => {
    const nuevoYear = Number(e.target.value)
    setYearFilter(nuevoYear)

    const mesDesde = `${nuevoYear}-01`
    const mesHasta =
      nuevoYear === currentYear
        ? `${nuevoYear}-${String(currentMonth).padStart(2, '0')}`
        : `${nuevoYear}-12`

    setDateFromMonth(mesDesde)
    setDateToMonth(mesHasta)
  }

  const handleDateFromChange = e => {
    const nuevoDesde = e.target.value
    setDateFromMonth(nuevoDesde)

    const [fromY, fromM] = nuevoDesde.split('-').map(Number)
    const [toY, toM] = dateToMonth.split('-').map(Number)
    if (fromY === toY && fromM > toM) {
      setDateToMonth(nuevoDesde)
    }
  }

  const handleDateToChange = e => {
    const nuevoHasta = e.target.value
    const [fromY, fromM] = dateFromMonth.split('-').map(Number)
    const [toY, toM] = nuevoHasta.split('-').map(Number)
    if (toY === fromY && toM < fromM) {
      setDateToMonth(dateFromMonth)
    } else {
      setDateToMonth(nuevoHasta)
    }
  }

  const handleExport = async email => {
    setIsExporting(true)
    try {
      await businessApi.exportLibroMayor({
        organizationId: currentBusiness.organizationId,
        businessId: currentBusiness.id,
        email,
        idCuenta: selectedAccount?.id || undefined,
        dateFrom: {
          year: parseInt(dateFromMonth.split('-')[0]),
          month: parseInt(dateFromMonth.split('-')[1])
        },
        dateTo: {
          year: parseInt(dateToMonth.split('-')[0]),
          month: parseInt(dateToMonth.split('-')[1])
        },
        searchQuery: comprobanteFilter || clienteFilter || undefined
      })
    } catch (error) {
      console.error('Error al exportar:', error)
      throw error
    } finally {
      setIsExporting(false)
    }
  }

  // Construye las opciones con "Todas" al inicio para el select
  const cuentasWithAll = useMemo(
    () => [{ id: 0, codigo: 'Todas', name: 'Todas las cuentas' }, ...acount],
    [acount]
  )

  const filteredData = useMemo(() => {
    return data
      .filter(row => {
        if (!comprobanteFilter) return true
        return row.idOrigin
          .toString()
          .toLowerCase()
          .includes(comprobanteFilter.trim().toLowerCase())
      })
      .filter(row => {
        if (!clienteFilter) return true
        const term = clienteFilter.trim().toLowerCase()
        const rut = row.informacion?.rutEmpresa?.toString().toLowerCase() || ''
        const razon = row.informacion?.razonSocial?.toString().toLowerCase() || ''
        return rut.includes(term) || razon.includes(term)
      })
  }, [data, comprobanteFilter, clienteFilter])

  const icons = [
    <button
      key='export-xlsx'
      className='w-10 h-10 text-green-700 font-bold cursor-pointer rounded-full shadow bg-white'
      onClick={() => setIsExportModalOpen(true)}
    >
      XLS
    </button>
  ]

  return (
    <>
      <HeadingTitle title='Libro Mayor' rightIcons={icons} iconHeight={32} />
      {selectedAccount && (
        <Text className='mb-6'>
          Cuenta{' '}
          <strong>{selectedAccount.id === 0 ? 'Todas las cuentas' : selectedAccount.name}</strong>
        </Text>
      )}
      <div className='flex flex-col gap-3 mb-6'>
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end'>
          <div className='flex flex-col w-full'>
            <label>Año</label>
            <select
              value={yearFilter}
              onChange={handleYearChange}
              className='w-full border border-gray-300 rounded-full px-3 py-1.5 text-sm'
            >
              {yearsOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className='flex flex-col w-full'>
            <label>Desde</label>
            <input
              type='month'
              value={dateFromMonth}
              onChange={handleDateFromChange}
              min={`${yearFilter}-01`}
              max={`${yearFilter}-12`}
              className={clsx(
                styles.monthInput,
                'w-full border border-border rounded-full px-3 py-1.5 text-sm capitalize'
              )}
            />
          </div>

          <div className='flex flex-col w-full'>
            <label>Hasta</label>
            <input
              type='month'
              value={dateToMonth}
              onChange={handleDateToChange}
              min={`${yearFilter}-01`}
              max={
                yearFilter === currentYear
                  ? `${yearFilter}-${String(currentMonth).padStart(2, '0')}`
                  : `${yearFilter}-12`
              }
              className={clsx(
                styles.monthInput,
                'w-full border border-gray-300 rounded-full px-3 py-1.5 text-sm capitalize'
              )}
            />
          </div>

          <CustomButton
            className='rounded-full h-8 w-full'
            color='primary'
            title='Buscar'
            onClick={refetch}
          />
        </div>

        <div className='flex justify-end'>
          <button
            className='flex items-center px-2 py-1 bg-surface/70 border border-border rounded hover:bg-border/30 transition'
            onClick={() => setShowFilters(prev => !prev)}
          >
            <KeyboardArrowDownIcon
              className={clsx('transition-transform', {
                'rotate-180': showFilters
              })}
            />
          </button>
        </div>
      </div>

      {showFilters && (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-3 items-end border border-border rounded-md p-4 bg-surface/70 mb-6'>
          <div className='w-full'>
            <label>Cuenta</label>
            <SearchableSelect
              options={cuentasWithAll}
              value={selectedAccount}
              onChange={opt => setSelectedAccount(opt)}
              placeholder='Selecciona o busca cuenta'
            />
          </div>
          <div className='flex flex-col w-full'>
            <input
              type='text'
              value={comprobanteFilter}
              onChange={e => setComprobanteFilter(e.target.value)}
              placeholder='Comprobante'
              className='w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
            />
          </div>
          <div className='flex flex-col w-full'>
            <input
              type='text'
              value={clienteFilter}
              onChange={e => setClienteFilter(e.target.value)}
              placeholder='Busca RUT o Razón Social'
              className='w-full border border-border rounded-full px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'
            />
          </div>
        </div>
      )}

      {isLoading ? (
        <div className='flex items-center justify-center pt-8 h-full'>
          <Lottie animationData={LoaderAdm} loop style={{ width: 300, height: 300 }} />
        </div>
      ) : (
        <div className='flex-1 h-[30vh]'>
          <Table
            rows={filteredData}
            className='h-[40vh]'
            lastElementRef={lastElementRef}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />
        </div>
      )}

      <Modal />
      <ExportToXlsxModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        title='Exportar Libro Mayor'
        description='Se enviará un archivo Excel con el libro mayor al email especificado'
        isLoading={isExporting}
      />
    </>
  )
}

export default ListLibroMayor
